import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { usePluginsQuery } from '../hooks/usePlugins';
import { PluginDetailDialog } from './PluginDetailDialog';
import type { GatewayPlugin } from '../types/plugin';

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} style={{ height: '48px', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-md)' }} />
      ))}
    </div>
  );
}

/**
 * Gateway 플러그인(GlobalFilter) 정적 레지스트리 (#621) — GET /api/eraf/gateway/plugins.
 *
 * ★ DB 조회가 아닌 prod apps/gateway 실배포 GlobalFilter 7종에 대한 사실(fact) 목록이다.
 * BE 에 CRUD 엔드포인트가 없으므로 조회 전용이며, 스코프별 플러그인 인스턴스 개념(Kong 스타일)은
 * 이 배포에 존재하지 않는다 — apps/gateway application.yml 의 전역 플래그로만 on/off 된다.
 */
export function PluginsListPage() {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<GatewayPlugin | null>(null);

  const { data, isLoading, isError, refetch } = usePluginsQuery();

  const displayName = (plugin: GatewayPlugin) => (i18n.language.startsWith('ko') ? plugin.nameKo : plugin.nameEn);

  const columns: DataTableColumn<GatewayPlugin>[] = [
    {
      key: 'name',
      header: t('gateway.plugins.columns.name'),
      width: '180px',
      render: (row) => <span>{displayName(row)}</span>,
    },
    {
      key: 'flagProperty',
      header: t('gateway.plugins.columns.flagProperty'),
      width: '260px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{row.flagProperty}</span>
      ),
    },
    {
      key: 'gCode',
      header: t('gateway.plugins.columns.gCode'),
      width: '90px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>{row.gCode}</span>
      ),
    },
    {
      key: 'description',
      header: t('gateway.plugins.columns.description'),
      render: (row) => (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {row.description}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('gateway.plugins.title')} breadcrumb={t('gateway.plugins.breadcrumb')} />

      {/* ★ 사실(fact) 목록 안내 배너 */}
      <div
        role="note"
        aria-label={t('gateway.plugins.factBanner')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          margin: '0 var(--spacing-6) var(--spacing-4)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-info-subtle, #EEF2FF)',
          border: '1px solid var(--color-info-border, #C7D2FE)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-info-text, #3730A3)',
        }}
      >
        <span aria-hidden="true">i</span>
        <span>{t('gateway.plugins.factBanner')}</span>
      </div>

      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ErrorState
            title={t('gateway.plugins.list.error')}
            message={t('gateway.shared.error.networkErrorMessage')}
            onRetry={() => refetch()}
          />
        ) : !data || data.length === 0 ? (
          <EmptyState title={t('gateway.plugins.list.empty')} description={t('gateway.plugins.list.emptyDescription')} />
        ) : (
          <DataTable columns={columns} rows={data} rowKey={(row) => row.key} onRowClick={(row) => setSelected(row)} />
        )}
      </div>

      {selected && (
        <PluginDetailDialog plugin={selected} displayName={displayName(selected)} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
